import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuthToken, clearTokens, isTokenExpired, getTokenRemainingTime } from '../utils/auth';
import { toast } from 'react-toastify';

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasLoggedOut = useRef(false);

  // Helper function to handle logout
  const handleLogout = useCallback((message: string) => {
    if (hasLoggedOut.current) return; // Prevent multiple logouts
    hasLoggedOut.current = true;
    toast.error(message);
    clearTokens();
    navigate('/');
    // Reset after a short delay to allow re-login
    setTimeout(() => {
      hasLoggedOut.current = false;
    }, 1000);
  }, [navigate]);

  useEffect(() => {
    // Check if user has valid tokens
    const authToken = getAuthToken();

    // If on login page and already authenticated, redirect to dashboard
    if (location.pathname === '/' && authToken && !isTokenExpired()) {
      navigate('/dashboard');
      return;
    }

    // Skip further auth checks on login page
    if (location.pathname === '/') {
      return;
    }

    if (!authToken) {
      // No token, redirect to login
      handleLogout('Please log in to continue');
      return;
    }

    // Check if token is already expired
    if (isTokenExpired()) {
      handleLogout('Session expired. Please log in again.');
      return;
    }

    // Set up timer to auto-logout when token expires
    const remainingTime = getTokenRemainingTime();
    console.log(`Token expires in ${Math.round(remainingTime / 1000 / 60)} minutes`);

    const expiryTimer = setTimeout(() => {
      handleLogout('Session expired. Please log in again.');
    }, remainingTime);

    // NOTE: Token refresh is temporarily disabled until backend is fixed

    // Set up global fetch interceptor for auth errors (without refresh)
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);

        // Skip auth check if on login page
        if (location.pathname === '/') {
          return response;
        }

        const requestInfo = args[0];
        let requestUrl = '';
        if (typeof requestInfo === 'string') {
          requestUrl = requestInfo;
        } else if (requestInfo instanceof Request) {
          requestUrl = requestInfo.url;
        } else if (requestInfo instanceof URL) {
          requestUrl = requestInfo.href;
        }
        const skipAuthLogout = requestUrl.includes('deleteWalletShopProduct');
        if (skipAuthLogout) {
          return response;
        }

        // Handle 401 Unauthorized - always logout
        if (response.status === 401) {
          handleLogout('Session expired. Please log in again.');
          return response;
        }

        // Skip auto-logout for 403s (can be API key or permission issues)
        if (response.status === 403) {
          return response;
        }

        // Handle other 4xx errors - check if it's an auth issue
        if (response.status >= 400 && response.status < 500) {
          const clonedResponse = response.clone();
          try {
            const data = await clonedResponse.json();
            // Check all possible message fields
            const message = (
              data.message ||
              data.Message ||
              data.error ||
              data.Error ||
              JSON.stringify(data)
            ).toLowerCase();

            // Check if it's an auth-related error
            if (
              message.includes('token') ||
              message.includes('unauthorized') ||
              message.includes('authentication') ||
              message.includes('forbidden') ||
              message.includes('access denied') ||
              message.includes('expired') ||
              message.includes('not authorized') ||
              message.includes('identity-based policy') ||
              message.includes('explicit deny') ||
              message.includes('deny')
            ) {
              handleLogout('Access denied. Please log in again.');
              return response;
            }
          } catch (e) {
            // JSON parse failed, ignore
          }
        }

        return response;
      } catch (error) {
        // Network error - don't throw, just log and handle gracefully
        console.error('Network error:', error);
        throw error;
      }
    };

    // Cleanup
    return () => {
      clearTimeout(expiryTimer);
      window.fetch = originalFetch;
    };
  }, [navigate, location.pathname, handleLogout]);

  return <>{children}</>;
};

export default AuthProvider;
